import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../components/common/HtmlContent';
import useHashScroll from '../hooks/useHashScroll';

/**
 * ResourcesPage Component
 * 
 * Displays all resources content like the legacy HTML site
 */
const ResourcesPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Resources intro content
  const resourcesIntroHtml = `
    <p>
      As the Product Development space continues to evolve, more and more resources are created and launched that can aid with the process of creating products - these can range from rapid prototyping, to audience data, tracking projects and launching MVPs. 
    </p>
    
    <p>
      This section contains resources that can help hugely in the Product R&D process.
    </p>
  `;

  return (
    <>
      {/* Resources Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/">R&D Toolkit</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/resources">Resources</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/resources">Resources</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={resourcesIntroHtml} />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Sections</div>
          </div>
          <div className="tool-section-list">
            <div><a className="tool-section-cta" href="#R&D">R&D</a></div>
            <div><a className="tool-section-cta" href="#websites">Websites</a></div>
            <div><a className="tool-section-cta" href="#project-tracking">Project Tracking</a></div>
            <div><a className="tool-section-cta" href="#further-resources">Further Resources</a></div>
          </div>
        </div>
      </div>

      {/* R&D Section */}
      <main className="case-study">
        <span className="case-tool-title" id="R&D">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="R&D"><span className="tool-title-number"><a href="#R&D">R&D</a></span></div>
          <div className="tool-title-nav"><Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;<i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;<Link to="/resources">Resources</Link></div>
          <div className="tool-title-link"><a href="#R&D"><i className="fas fa-link fa-2x"></i></a></div>
        </div>
        <div className="linebreak"></div>

        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <p>
                A dump for R&D resources I currently use, or could be useful in the future for rapid prototyping, experimentation and development.
              </p>
              <h4>R&D Resources</h4>
            </div>
          </div>
        </div>

        <div id="tabs-grandparent" className="tabs">
          <input type="radio" name="tabs-r&d" id="tab-r&d-prototyping" defaultChecked />
          <label htmlFor="tab-r&d-prototyping">Prototyping</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              A large part of the Product R&D process is rapid experimentation and prototyping, these sites will help with the required efficiency and speed that is vital to successfully moving forwards.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.invisionapp.com" className="link-table">Invision</a></td>
                  <td>Create interactive prototypes from Sketch or Figma design files for rapid testing and feedback.</td>
                </tr>
                <tr>
                  <td><a href="https://www.axure.com" className="link-table">Axure RP 9</a></td>
                  <td>Prototyping software that helps plan and then seamlessly handoff to developers.</td>
                </tr>
                <tr>
                  <td><a href="https://principleformac.com" className="link-table">Principle</a></td>
                  <td>Easily create animated designs and interactive interfaces.</td>
                </tr>
                <tr>
                  <td><a href="https://www.framer.com" className="link-table">Framer</a></td>
                  <td>Rapid realistic prototyping - import designs that can be made interactive.</td>
                </tr>
                <tr>
                  <td><a href="https://www.flinto.com" className="link-table">Flinto</a></td>
                  <td>Interactive prototyping that can import Sketch/Figma designs.</td>
                </tr>
                <tr>
                  <td><a href="https://proto.io" className="link-table">Proto.io</a></td>
                  <td>Hi-fidelity prototypes that look and feel like a finished product.</td>
                </tr>
                <tr>
                  <td><a href="https://origami.design" className="link-table">Origami</a></td>
                  <td>Software that covers design, prototyping and animation as a single package.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-r&d" id="tab-r&d-audience" />
          <label htmlFor="tab-r&d-audience">Audience</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Taking the time to understand what an audience is using a product for is incredibly important to discovering problems and opportunities.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.hotjar.com" className="link-table">Hotjar</a></td>
                  <td>Identify exactly what users are doing with a product.</td>
                </tr>
                <tr>
                  <td><a href="https://analytics.google.com" className="link-table">Google Analytics</a></td>
                  <td>Raw and vast data around product usage including where users are referred from, how long they are on the site for, and what they are looking for.</td>
                </tr>
                <tr>
                  <td><a href="https://www.fullstory.com" className="link-table">FullStory</a></td>
                  <td>Fully understand the existing digital experience of your products for users.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-r&d" id="tab-r&d-design" />
          <label htmlFor="tab-r&d-design">Design</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Efficiently taking insights, ideas and user requirements and translating them in to features and a user interface is very important, the various design software found here can solve that and more.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.sketch.com" className="link-table">Sketch</a></td>
                  <td>Design and manage different fidelity elements, symbols and screens using that can easily be exported for prototyping or to code.</td>
                </tr>
                <tr>
                  <td><a href="https://www.figma.com" className="link-table">Figma</a></td>
                  <td>UI design software that encourages collaboration as well as the ability to easily export for prototyping and coding.</td>
                </tr>
                <tr>
                  <td><a href="https://marvelapp.com" className="link-table">Marvel</a></td>
                  <td>Design, prototype and easily handoff projects.</td>
                </tr>
                <tr>
                  <td><a href="https://www.uxpin.com" className="link-table">UX Pin</a></td>
                  <td>Create high fidelity designs and make them interactive inside a single package.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-r&d" id="tab-r&d-coding" />
          <label htmlFor="tab-r&d-coding">Coding</label>
          <div className="tab">
            <div className="content-r&d-coding">
              <div className="tab-resources-container">
                <div className="resources-column">
                  <table>
                    <tbody>
                      <tr>
                        <th>Development Environments</th>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://code.visualstudio.com/" className="link-table" target="_blank" rel="noopener noreferrer">VS Code</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://www.jetbrains.com/webstorm/" className="link-table" target="_blank" rel="noopener noreferrer">WebStorm</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://repl.it/" className="link-table" target="_blank" rel="noopener noreferrer">Repl.it</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://codesandbox.io/" className="link-table" target="_blank" rel="noopener noreferrer">CodeSandbox</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="resources-column">
                  <table>
                    <tbody>
                      <tr>
                        <th>React Resources</th>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://reactjs.org/docs/getting-started.html" className="link-table" target="_blank" rel="noopener noreferrer">React Docs</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://reactrouter.com/web/guides/quick-start" className="link-table" target="_blank" rel="noopener noreferrer">React Router</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://redux.js.org/introduction/getting-started" className="link-table" target="_blank" rel="noopener noreferrer">Redux</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://www.freecodecamp.org/news/the-react-handbook-b71c27b0a795/" className="link-table" target="_blank" rel="noopener noreferrer">React Handbook</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="resources-column">
                  <table>
                    <tbody>
                      <tr>
                        <th>Learning Platforms</th>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://www.freecodecamp.org/" className="link-table" target="_blank" rel="noopener noreferrer">freeCodeCamp</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://www.codecademy.com/" className="link-table" target="_blank" rel="noopener noreferrer">Codecademy</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://egghead.io/" className="link-table" target="_blank" rel="noopener noreferrer">Egghead.io</a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://frontendmasters.com/" className="link-table" target="_blank" rel="noopener noreferrer">Frontend Masters</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <input type="radio" name="tabs-r&d" id="tab-r&d-research" />
          <label htmlFor="tab-r&d-research">Research</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Being able to scale up and efficiently conduct research will help with the speed and success of a project. Leaning on these products can help during rapid development.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://ethn.io" className="link-table">Ethn.io</a></td>
                  <td>Recruit research participants.</td>
                </tr>
                <tr>
                  <td><a href="https://www.hellopingpong.com" className="link-table">Ping Pong</a></td>
                  <td>User interviews and usability testing.</td>
                </tr>
                <tr>
                  <td><a href="https://docs.google.com/forms/" className="link-table">Google Forms</a></td>
                  <td>Use to conduct a screener survey to collect research participants and general data.</td>
                </tr>
                <tr>
                  <td><a href="https://www.surveymonkey.com" className="link-table">Survey Monkey</a></td>
                  <td>Use for more in depth surveying of an audience, and use their other services for further expansion of particpants.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-r&d" id="tab-r&d-mvp" />
          <label htmlFor="tab-r&d-mvp">MVP</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Being able to test an hypothesis with a real audience is the pinnacle of R&D, and MVPs are one of the most valuable methofs for achieving this.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://glideapps.com" className="link-table">Glide</a></td>
                  <td>Create an app quickly from a Google Sheet - no coding needed for a basic MVP or mini-product.</td>
                </tr>
                <tr>
                  <td><a href="https://bubble.io" className="link-table">Bubble</a></td>
                  <td>Build a web app without code - use templates and workflows with a GUI to create a solution.</td>
                </tr>
                <tr>
                  <td><a href="https://zapier.com" className="link-table">Zapier</a></td>
                  <td>Interconnect numerous apps to automate all sorts of tasks that can make prototyping possible with off the shelf web apps.</td>
                </tr>
                <tr>
                  <td><a href="https://www.carrd.co" className="link-table">Carrd</a></td>
                  <td>Create landing pages quickly to test demand before building anything.</td>
                </tr>
                <tr>
                  <td><a href="https://expo.io" className="link-table">Expo</a></td>
                  <td>Create iOS and Android apps from React Native code for rapid mobile app development.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Websites Section */}
      <main className="case-study">
        <span className="case-tool-title" id="websites">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="websites"><span className="tool-title-number"><a href="#websites">Websites</a></span></div>
          <div className="tool-title-nav"><Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;<i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;<Link to="/resources">Resources</Link></div>
          <div className="tool-title-link"><a href="#websites"><i className="fas fa-link fa-2x"></i></a></div>
        </div>
        <div className="linebreak"></div>

        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <p>
                General websites that contain helpful information or tools that can aid the development process, whether it be theory behind R&D, tools, templates or other valuable insights.
              </p>
              <h4>Websites</h4>
            </div>
          </div>
        </div>

        <div id="tabs-grandparent" className="tabs">
          <input type="radio" name="tabs-websites" id="tab-websites-tools" defaultChecked />
          <label htmlFor="tab-websites-tools">Tools & Templates</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Various sites that contain a wealth of tools and templates that can help with Product R&D - also good to follow as usually leading on innovation thought.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.designmethodsfinder.com/" className="link-table">Design Methods Finder</a></td>
                  <td>Searchable database of Design Methods and Tools.</td>
                </tr>
                <tr>
                  <td><a href="https://www.dt-toolbook.com/tools-eng" className="link-table">DT Toolbook</a></td>
                  <td>Design Thinking Tools.</td>
                </tr>
                <tr>
                  <td><a href="https://www.dx.mba" className="link-table">dx.mba</a></td>
                  <td>Strategic innovation toolkit.</td>
                </tr>
                <tr>
                  <td><a href="https://www.designlibs.com" className="link-table">Design Libs</a></td>
                  <td>Tools that help defining what we want to do before ideation.</td>
                </tr>
                <tr>
                  <td><a href="https://www.designkit.org" className="link-table">IDEO Design Kit</a></td>
                  <td>Various design related tools from one of the oldest and most innovative design agencies.</td>
                </tr>
                <tr>
                  <td><a href="https://designsprintkit.withgoogle.com" className="link-table">Google Sprint Kit</a></td>
                  <td>Toolkit focused on design sprints.</td>
                </tr>
                <tr>
                  <td><a href="https://www.thisisservicedesigndoing.com" className="link-table">This is Service Design Doing</a></td>
                  <td>Tools to aid with service design.</td>
                </tr>
                <tr>
                  <td><a href="https://pdmethods.com" className="link-table">Product Discovery Methods</a></td>
                  <td>Design thinking tools that specifically help with the product discovery and innovation process.</td>
                </tr>
                <tr>
                  <td><a href="https://usefyi.com/templates/ux-research-plan/" className="link-table">fyi Resource Depository</a></td>
                  <td>Wealth of usable tools, templates and other resources for all manners of Product Development.</td>
                </tr>
                <tr>
                  <td><a href="https://www.designabetterbusiness.tools" className="link-table">Design a Better Business</a></td>
                  <td>Design and business tools for startups and innovation.</td>
                </tr>
                <tr>
                  <td><a href="http://designthinkmakebreakrepeat.com" className="link-table">Design Think Make Break Repeat</a></td>
                  <td>Design thinking tools and templates.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-websites" id="tab-websites-design" />
          <label htmlFor="tab-websites-design">Delivery</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              One of the hardest parts of Product Development is ensuring teams work efficiently and harmoniously when delivering the project, these websites cover the best practices and methods for doing so.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.atlassian.com/agile/" className="link-table">Atlassian Agile</a></td>
                  <td>Excellent glossary of terms and processes used within Agile. Was particularly helpful whilst creating this toolkit.</td>
                </tr>
                <tr>
                  <td><a href="https://www.scrumguides.org" className="link-table">Scrum Guide</a></td>
                  <td>Overview of Scrum, including all of the components that help with the Scrum process.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-websites" id="tab-websites-research" />
          <label htmlFor="tab-websites-research">Theory</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              A lot of approaches and ideas stem from theories and methodologies that help shape the eventual process, as like all things, Product Development/Innovation/Start-up theory is ever evolving and these resources are good places to keep track of changes and new thinking.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://dschool.stanford.edu/" className="link-table">Stanford Design School</a></td>
                  <td>As with many disciplines, Stanford are thought leads and design thinking is no exception.</td>
                </tr>
                <tr>
                  <td><a href="http://jjg.net" className="link-table">Jesse James Garrett</a></td>
                  <td>One of the original design thinkers and UX pioneers, JJG has plenty of insights and thoughts that can help shape thinking and approaches.</td>
                </tr>
                <tr>
                  <td><a href="https://www.nngroup.com" className="link-table">NN Group</a></td>
                  <td>As well as a wealth of tools and templates, NN Group (founded by Don Normal and Jakob Nielsen) have a vast amount of articles, talks and other theory around User Experience and other digital processes.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Project Tracking Section */}
      <main className="case-study">
        <span className="case-tool-title" id="project-tracking">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="project-tracking"><span className="tool-title-number"><a href="#project-tracking">Project Tracking</a></span></div>
          <div className="tool-title-nav"><Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;<i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;<Link to="/resources">Resources</Link></div>
          <div className="tool-title-link"><a href="#project-tracking"><i className="fas fa-link fa-2x"></i></a></div>
        </div>
        <div className="linebreak"></div>

        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <p>
                It's important to keep on top of a project, collaborating and being transparent as to progress. Fortunately there are better tools available than just a simple spreadsheet (which can still work well for a variety of tasks). These tools are ever evolving, with newer products often launching often to help level up the process.
              </p>
              <h4>Project Tracking Resources</h4>
            </div>
          </div>
        </div>

        <div id="tabs-grandparent" className="tabs">
          <input type="radio" name="tabs-project-tracking" id="tab-project-tracking-progress" defaultChecked />
          <label htmlFor="tab-project-tracking-progress">Progress</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Tracking progress, encouraging transparency and keeping teams on the same page is incredibly important and these resources are here to solve that problem.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.atlassian.com/software/jira" className="link-table">Jira</a></td>
                  <td>Industry favoured platform for Agile software development.</td>
                </tr>
                <tr>
                  <td><a href="https://www.trello.com" className="link-table">Trello</a></td>
                  <td>Team organisation for project tracking.</td>
                </tr>
                <tr>
                  <td><a href="https://hygger.io" className="link-table">Hygger</a></td>
                  <td>Kanban software with roadmaps and backlog and other tracking features.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-project-tracking" id="tab-project-tracking-logging" />
          <label htmlFor="tab-project-tracking-logging">Logging</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              The Product Development process can create a lot of information, issues, thoughts, ideas and other things that need a central logging place, these resources can help beyond the normal spreadsheet or note taking app.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.miro.com" className="link-table">Miro</a></td>
                  <td>Highly flexible whiteboard software. Featured heavily in this toolkit, it's great for collaboration, logging and idea collection.</td>
                </tr>
                <tr>
                  <td><a href="https://airtable.com" className="link-table">Airtable</a></td>
                  <td>At a base level, a highly flexible way of logging things, but could be used in more sophisticated ways to imagine products and projects.</td>
                </tr>
                <tr>
                  <td><a href="https://www.notion.so" className="link-table">Notion</a></td>
                  <td>All encompassing powerful workspace for individuals or teams. Can be used across an organisation for a variety of tasks that require logging and sharing of information.</td>
                </tr>
                <tr>
                  <td><a href="https://www.ideo.com/post/shape" className="link-table">IDEO Shape</a></td>
                  <td>"Shape: A Visual, Collaborative Space to Build, Test, and Refine your Ideas"</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-project-tracking" id="tab-project-tracking-product" />
          <label htmlFor="tab-project-tracking-product">Product</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              The Product function in an organisation is very unique, with no 2 product people ever being the same - whilst their remit is usually spread across a number of areas, there are some core functions that are required and these resources can help cover that.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://www.prodpad.com" className="link-table">ProdPad</a></td>
                  <td>Software to help cover the base Product role, including ideas management, backlogs, customer feedback and strategic planning.</td>
                </tr>
                <tr>
                  <td><a href="https://www.aha.io" className="link-table">Aha!</a></td>
                  <td>Resource focused on strategic roadmaps, along with a number of other tools that can help prioritise features.</td>
                </tr>
                <tr>
                  <td><a href="https://www.pendo.io" className="link-table">Pendo</a></td>
                  <td>Gather in-depth insights in to the existing user's experiences to help drive the roadmap for future features.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-project-tracking" id="tab-project-tracking-research" />
          <label htmlFor="tab-project-tracking-research">Research</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Collating, tracking and retrospectively looking at research is very important as a means of improving the likelihood of a successful product, and these resources can help with that.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://userbitapp.com" className="link-table">UserBit</a></td>
                  <td>A platform for organising research data, including user interviews, surveys and mapping users.</td>
                </tr>
                <tr>
                  <td><a href="https://roamresearch.com" className="link-table">Roam</a></td>
                  <td>Moving quickly whilst developing products is the name of the game, but sometimes this can create knowledge gaps. Roam can help link subjects together as a kind of directory to refer back to when making decisions.</td>
                </tr>
                <tr>
                  <td><a href="https://getenjoyhq.com" className="link-table">EnjoyHQ</a></td>
                  <td>Centralised place for all research data, with standardised formatting for consistent collaboration.</td>
                </tr>
                <tr>
                  <td><a href="https://dovetailapp.com" className="link-table">Dovetail</a></td>
                  <td>Analyse data and then collaborate on the insights all in a single research repository.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Further Resources Section */}
      <main className="case-study">
        <span className="case-tool-title" id="further-resources">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="further-resources"><span className="tool-title-number"><a href="#further-resources">Further Resources</a></span></div>
          <div className="tool-title-nav"><Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;<i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;<Link to="/resources">Resources</Link></div>
          <div className="tool-title-link"><a href="#further-resources"><i className="fas fa-link fa-2x"></i></a></div>
        </div>
        <div className="linebreak"></div>

        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <p>
                Miscellaneous resources that are helpful in general and worthy of future reference.
              </p>
              <h4>Further Resources</h4>
            </div>
          </div>
        </div>

        <div id="tabs-grandparent" className="tabs">
          <input type="radio" name="tabs-further-resources" id="tab-further-resources-presentations" defaultChecked />
          <label htmlFor="tab-further-resources-presentations">Presentations</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://docs.google.com/presentation/d/1kMROhf-S6z0hLKb7Km0PddUff-wiYkuD5QCNeD8t-Ns/edit#slide=id.g827ccf4a3e_0_8" className="link-table">Stamford Design School Design Thinking Facilitator Presentation</a></td>
                  <td>A number of tools and techniques packaged up as facilitating presentation for teaching and running a Design Thinking workshop.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-further-resources" id="tab-further-resources-articles" />
          <label htmlFor="tab-further-resources-articles">Articles</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://hbr.org/2017/06/the-4-types-of-innovation-and-the-problems-they-solve" className="link-table">The 4 Types of Innovation and the Problems They Solve</a></td>
                  <td>Nice overview of Innovation models, with references to resources and discussion around big scientific backed innovation.</td>
                </tr>
                <tr>
                  <td><a href="https://zettelkasten.de/posts/trust-the-process-nickmilo22/" className="link-table">Trust the Process</a></td>
                  <td>We can easily get distracted, get overwhelmed or get disillusioned with a project, this is a reminder to trust the process.</td>
                </tr>
                <tr>
                  <td><a href="https://www.invisionapp.com/inside-design/ux-strategy-guide/" className="link-table">This UX Strategy Guide helps designers make better decisions</a></td>
                  <td>Article on helping UX designers get onboard with corporate/business decision making that can help inform their designs in a positive way.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <input type="radio" name="tabs-further-resources" id="tab-further-resources-future" />
          <label htmlFor="tab-further-resources-future">Future Resource Log</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <p>
              Resources that could be helpful in the future, but don't currently have a home or I haven't explored them fully.
            </p>
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools-parent">
                  <td>Resource</td>
                  <td>Description</td>
                </tr>
                <tr>
                  <td><a href="https://fortelabs.co/blog/para/" className="link-table">PARA Method</a></td>
                  <td>System for managing everything. Uses a format of Areas of interest, with individual projects that can help accomplish and further than Area.</td>
                </tr>
                <tr>
                  <td><a href="https://www.buildingasecondbrain.com" className="link-table">Building a Second Brain</a></td>
                  <td>I started this toolkit as a means to offload my thoughts and knowledge on Product Development and organise them in a way that allows me to quickly refer back and remind myself. This resource explores that concept further.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </>
  );
};

export default ResourcesPage; 